import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Facebook, Send, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/Primitives'; // <-- Убедитесь, что Primitives.jsx создан (из шага 3)

// === Данные навигации (бывшие в mockData.js) ===
const navigationItems = [
  { name: 'Головна', path: '/' },
  { name: 'Моделям', path: '/models' },
  { name: 'Партнерам', path: '/partners' },
  { name: 'Контакти', path: '/contact' }
];

// === Внутренний компонент Navbar ===
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-black/95 backdrop-blur-md py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="text-2xl font-bold">
              <span className="text-emerald-500">BM</span>
              <span className="text-white">Group</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors duration-200 relative group ${
                  location.pathname === item.path
                    ? 'text-emerald-500'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {item.name}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-500 transition-all duration-300 group-hover:w-full ${
                    location.pathname === item.path ? 'w-full' : ''
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="https://t.me/bmg_managerrrr"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-6 transition-all duration-300 hover:scale-105"
              >
                ЗВ'ЯЗАТИСЯ
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4 animate-fadeIn">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-emerald-500'
                    : 'text-gray-300'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <a
              href="https://t.me/bmg_managerrrr"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white w-full">
                ЗВ'ЯЗАТИСЯ
              </Button>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// === Внутренний компонент Footer ===
const Footer = () => {
  return (
    <footer className="bg-black border-t border-white/10 mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="text-2xl font-bold">
              <span className="text-emerald-500">BM</span>
              <span className="text-white">Group</span>
            </div>
            <p className="text-gray-400 text-sm">
              Професійне управління профілями на міжнародних платформах. Прозоро, системно та з повагою до вашої приватності.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://t.me/bmg_managerrrr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-emerald-500 transition-colors"
                aria-label="Telegram"
              >
                <Send size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Навігація</h3>
            <ul className="space-y-2">
              {navigationItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-gray-400 hover:text-emerald-500 text-sm transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Контакти</h3>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2 text-gray-400 text-sm">
                <Phone size={16} className="text-emerald-500 flex-shrink-0" />
                <a href="tel:+380634120490" className="hover:text-emerald-400 transition-colors">
                  +38 (063) 412-04-90
                </a>
              </li>
              <li className="flex items-center space-x-2 text-gray-400 text-sm">
                <Mail size={16} className="text-emerald-500 flex-shrink-0" />
                <a href="mailto:info@bmgroup.ua" className="hover:text-emerald-400 transition-colors">
                  info@bmgroup.ua
                </a>
              </li>
              <li className="flex items-center space-x-2 text-gray-400 text-sm">
                <Send size={16} className="text-emerald-500 flex-shrink-0" />
                <a
                  href="https://t.me/bmg_managerrrr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-emerald-400 transition-colors"
                >
                  @bmg_managerrrr
                </a>
              </li>
            </ul>
          </div>

          {/* Working Hours */}
          <div>
            <h3 className="text-white font-semibold mb-4">Години роботи</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>Понеділок - П'ятниця</li>
              <li className="text-emerald-500">10:00 - 19:00</li>
              <li className="mt-4">Субота - Неділя</li>
              <li className="text-gray-500">Вихідний</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-400 text-sm">
            © 2022–{new Date().getFullYear()} BMGroup. Всі права захищені.
          </p>
          <Link
            to="/privacy"
            className="text-gray-500 hover:text-emerald-500 text-sm transition-colors"
          >
            Політика конфіденційності
          </Link>
        </div>
      </div>
    </footer>
  );
};

// === Основной Layout (экспорт) ===
const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen bg-black text-white">
      <Navbar />
      <main className="flex-grow w-full">
        {children}
      </main>

      <Footer />
    </div>
  );
};

export default Layout;